import { useState } from 'react';
import { GiftCard, CreateGiftCardData } from '../../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { cn } from '@/lib/utils';

interface GiftCardFormProps {
  card?: GiftCard;
  onSave: (data: CreateGiftCardData & { currentBalance?: number }) => void;
  onCancel: () => void;
}

export function GiftCardForm({ card, onSave, onCancel }: GiftCardFormProps) {
  const [name, setName] = useState(card?.name || '');
  const [initialBalance, setInitialBalance] = useState(
    card ? parseFloat(card.initialBalance).toString() : ''
  );
  const [currentBalance, setCurrentBalance] = useState(
    card ? parseFloat(card.currentBalance).toString() : ''
  );
  const [expireDate, setExpireDate] = useState(
    card?.expireDate ? new Date(card.expireDate).toISOString().split('T')[0] : ''
  );
  const [quantity, setQuantity] = useState('1');
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const validateField = (fieldName: string, value: any): string | null => {
    switch (fieldName) {
      case 'initialBalance':
        if (!value || value.trim() === '') {
          return 'Начальный баланс обязателен';
        }
        const balance = parseFloat(value);
        if (isNaN(balance) || balance <= 0) {
          return 'Начальный баланс должен быть положительным числом';
        }
        return null;
      case 'currentBalance':
        if (card && value !== undefined && value !== '') {
          const balanceValue = parseFloat(value);
          if (isNaN(balanceValue) || balanceValue < 0) {
            return 'Текущий баланс должен быть неотрицательным числом';
          }
          const initialBalanceValue = parseFloat(initialBalance);
          if (balanceValue > initialBalanceValue) {
            return 'Текущий баланс не может быть больше начального баланса';
          }
        }
        return null;
      case 'name':
        if (value && value.length > 100) {
          return 'Номер сертификата не может быть длиннее 100 символов';
        }
        return null;
      case 'expireDate':
        if (value) {
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            return 'Неверный формат даты';
          }
          if (date < new Date()) {
            return 'Дата истечения не может быть в прошлом';
          }
        }
        return null;
      case 'quantity':
        if (!card) {
          const qty = parseInt(value, 10);
          if (isNaN(qty) || qty < 1 || qty > 100) {
            return 'Количество должно быть от 1 до 100';
          }
        }
        return null;
      default:
        return null;
    }
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    const error = validateField(fieldName, value);
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: error || '',
    }));
    if (error) {
      setFormError(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    
    const errors: Record<string, string> = {};
    
    if (!card) {
      const initialBalanceError = validateField('initialBalance', initialBalance);
      if (initialBalanceError) errors.initialBalance = initialBalanceError;
      
      const quantityValue = parseInt(quantity, 10);
      if (quantityValue > 1 && name) {
        errors.name = 'При создании нескольких сертификатов номер не может быть указан';
      }
    }
    
    if (card) {
      const currentBalanceError = validateField('currentBalance', currentBalance);
      if (currentBalanceError) errors.currentBalance = currentBalanceError;
    }
    
    if (name) {
      const nameError = validateField('name', name);
      if (nameError) errors.name = nameError;
    }
    
    if (expireDate) {
      const expireDateError = validateField('expireDate', expireDate);
      if (expireDateError) errors.expireDate = expireDateError;
    }
    
    if (!card) {
      const quantityError = validateField('quantity', quantity);
      if (quantityError) errors.quantity = quantityError;
    }
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFormError('Пожалуйста, исправьте ошибки в форме');
      return;
    }
    
    const saveData = {
      name: name || undefined,
      initialBalance: parseFloat(initialBalance),
      expireDate: expireDate || undefined,
      currentBalance: card ? parseFloat(currentBalance) : undefined,
      quantity: !card ? parseInt(quantity, 10) : undefined,
    };
    
    onSave(saveData);
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{card ? 'Редактировать сертификат' : 'Создать сертификат'}</DialogTitle>
        </DialogHeader>
        {formError && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          {card ? (
            <div className="space-y-2">
              <Label>Номер сертификата</Label>
              <Input
                type="text"
                value={name}
                disabled
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Номер сертификата (опционально)</Label>
              <Input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  handleFieldChange('name', e.target.value);
                }}
                placeholder="Оставьте пустым для автоматической генерации"
                disabled={parseInt(quantity, 10) > 1}
                className={cn(fieldErrors.name && "border-destructive")}
              />
              {fieldErrors.name && (
                <p className="text-sm text-destructive">{fieldErrors.name}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {parseInt(quantity, 10) > 1 
                  ? 'При создании нескольких сертификатов номера генерируются автоматически'
                  : 'Если не указан, номер будет сгенерирован автоматически из 8 цифр'}
              </p>
            </div>
          )}
          {!card && (
            <div className="space-y-2">
              <Label>Количество сертификатов *</Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={quantity}
                onChange={(e) => {
                  setQuantity(e.target.value);
                  handleFieldChange('quantity', e.target.value);
                }}
                required
                className={cn(fieldErrors.quantity && "border-destructive")}
              />
              {fieldErrors.quantity && (
                <p className="text-sm text-destructive">{fieldErrors.quantity}</p>
              )}
            </div>
          )}
          <div className="space-y-2">
            <Label>Начальный баланс (₽) *</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={initialBalance}
              onChange={(e) => {
                setInitialBalance(e.target.value);
                handleFieldChange('initialBalance', e.target.value);
              }}
              required
              disabled={!!card}
              className={cn(fieldErrors.initialBalance && "border-destructive")}
            />
            {fieldErrors.initialBalance && (
              <p className="text-sm text-destructive">{fieldErrors.initialBalance}</p>
            )}
          </div>
          {card && (
            <>
              <div className="space-y-2">
                <Label>Текущий баланс (₽) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max={parseFloat(initialBalance) || undefined}
                  value={currentBalance}
                  onChange={(e) => {
                    setCurrentBalance(e.target.value);
                    handleFieldChange('currentBalance', e.target.value);
                  }}
                  required
                  className={cn(fieldErrors.currentBalance && "border-destructive")}
                />
                {fieldErrors.currentBalance && (
                  <p className="text-sm text-destructive">{fieldErrors.currentBalance}</p>
                )}
              </div>
            </>
          )}
          <div className="space-y-2">
            <Label>Срок действия</Label>
            <Input
              type="date"
              value={expireDate}
              onChange={(e) => {
                setExpireDate(e.target.value);
                handleFieldChange('expireDate', e.target.value);
              }}
              className={cn(fieldErrors.expireDate && "border-destructive")}
            />
            {fieldErrors.expireDate && (
              <p className="text-sm text-destructive">{fieldErrors.expireDate}</p>
            )}
            {!card && (
              <p className="text-xs text-muted-foreground">
                Если не указан, будет установлен автоматически из настроек
              </p>
            )}
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <Button type="submit">
              {card ? 'Сохранить' : 'Создать'}
            </Button>
            <Button type="button" variant="destructive" onClick={onCancel}>
              Отмена
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
